import React from 'react';
import { AnimatedLoader, Button } from '@ditioas/web-ui';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export interface PDFGenerationState {
  [toastId: string]: {
    idsToGenerate: string[];
    generatedIds: string[];
  };
}

/**
 * Iterate through all entries in the PDF generation state and update the state for the given itemId
 * @param pdfGenerationState - The PDF generation state
 * @param itemId - The itemId to update
 * @returns The updated PDF generation state
 */
export const updatePDFGenerationState = (
  pdfGenerationState: PDFGenerationState,
  itemId: string
) => {
  Object.entries(pdfGenerationState).forEach(([toastId, entry]) => {
    if (
      entry.idsToGenerate.includes(itemId) &&
      !entry.generatedIds.includes(itemId)
    ) {
      // eslint-disable-next-line no-param-reassign
      pdfGenerationState[toastId] = {
        idsToGenerate: entry.idsToGenerate.filter((id) => id !== itemId),
        generatedIds: [...entry.generatedIds, itemId],
      };
    }
  });

  return pdfGenerationState;
};

interface PDFGenerationToastProps {
  toastId: string | number;
  retryDownloadPdfs: (
    idsToGenerate: string[],
    existingToastId?: string | number
  ) => Promise<{ file: File; filename: string } | null>;
  pdfGenerationState: PDFGenerationState;
  setPdfGenerationState: (
    newState:
      | PDFGenerationState
      | ((prevState: PDFGenerationState) => PDFGenerationState)
  ) => void;
}

const PDFGenerationToast = ({
  toastId,
  retryDownloadPdfs,
  pdfGenerationState,
  setPdfGenerationState,
}: PDFGenerationToastProps) => {
  const { t } = useTranslation();

  const [canRetry, setCanRetry] = React.useState(false);
  const [downloadingPdfs, setDownloadingPdfs] = React.useState(false);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (pdfGenerationState[toastId]?.idsToGenerate.length) {
      timeout = setTimeout(() => {
        setCanRetry(true);
      }, 60000);
    }

    if (!pdfGenerationState[toastId]?.idsToGenerate.length && timeout) {
      clearTimeout(timeout);
    }

    return () => clearTimeout(timeout);
  }, [pdfGenerationState[toastId]?.idsToGenerate]);

  // Cleanup toast when component unmounts
  React.useEffect(
    () => () => {
      setPdfGenerationState((prev) => {
        const newState = { ...prev };
        delete newState[toastId];
        return newState;
      });
    },
    []
  );

  if (!pdfGenerationState[toastId]) {
    return null;
  }

  const { idsToGenerate, generatedIds } = pdfGenerationState[toastId];

  const numberOfPdfsToGenerate = idsToGenerate.length;

  let heading = t('PdfGeneration.AwaitingGeneration', {
    count: numberOfPdfsToGenerate,
  });
  if (numberOfPdfsToGenerate === 0) {
    heading = t('PdfGeneration.AllReady');
  } else if (canRetry) {
    heading = t('PdfGeneration.TookLonger');
  }

  return (
    <>
      <p className="heading-sm">{heading}</p>
      {!canRetry &&
      numberOfPdfsToGenerate !== 0 &&
      idsToGenerate.length !== 0 ? (
        <AnimatedLoader className="mx-auto h-9 w-9" />
      ) : (
        <Button
          disabled={
            downloadingPdfs || (numberOfPdfsToGenerate !== 0 && !canRetry)
          }
          onClick={async () => {
            setDownloadingPdfs(true);
            setCanRetry(false);
            const result = await retryDownloadPdfs(
              [...idsToGenerate, ...generatedIds],
              toastId
            );
            setDownloadingPdfs(false);

            if (result?.file) {
              try {
                const isFileSaverSupported = !!new Blob();
                saveAs(result.file, result.filename);
                toast.dismiss(toastId);
              } catch (e) {
                alert('This feature is not supported by your browser.');
              }
            }
          }}
          className="dark w-full"
        >
          {canRetry && numberOfPdfsToGenerate !== 0
            ? t('PdfGeneration.RetryDownload')
            : t('PdfGeneration.DownloadPDFs', {
                count: generatedIds.length,
              })}
        </Button>
      )}
    </>
  );
};

export default PDFGenerationToast;
